<script>
  import { fly } from "svelte/transition";
  import { onMount, onDestroy, afterUpdate, createEventDispatcher } from "svelte";
  import * as yup from "yup";
  import { account, appSession, appConstants } from "../stores/app.js";
  import {
    ScrollingEnable,
    ScrollingDisable,
    FormatPilotName,
    GetStateList,
    getStateFromZip,
  } from "../library/util.js";
  import Select from "svelte-select";
  import MyModal from "../components/MyModal.svelte";
  import RestPilots from "../components/RestPilots.svelte";

  const dispatch = createEventDispatcher();

  let busy = true;
  let myModal;
  let restPilots;
  let pilot = null;

  let form = {
    dirty: false,
    valid: true,
    submitted: false,

    data: {
      phone: "",
      billing_city: "",
      billing_state: "",
      billing_country: "",
      billing_postcode: "",
    },

    schema: yup.object().shape({
      billing_city: yup.string().required({ message: "Required" }),
      billing_state: yup.string().required({ message: "Required" }),
      billing_postcode: yup
        .string()
        .test("billing_postcode", "Invalid for selected state", (value) => {
          let state = getStateFromZip(value);
          if (!state) return false;
          if (state.code != form.data.billing_state) return false;
          else return true;
        })
        .required({ message: "Required" }),
    }),
    errors: null,
  };

  let states = [];
  let stateList = GetStateList();
  for (let code in stateList) {
    states.push({ value: code, label: stateList[code] });
  }

  let stateSelectedValue = null;
  $: {
    if (stateSelectedValue != null) form.data.billing_state = stateSelectedValue.value;
  }

  onMount(async () => {
    ScrollingDisable();

    try {
      pilot = await restPilots.GetPilots($appSession.pilotId);
      form.data.phone = pilot.phone;
      form.data.billing_suburb = pilot.billing_suburb;
      form.data.billing_city = pilot.billing_city;
      form.data.billing_state = pilot.billing_state;
      form.data.billing_country = pilot.billing_country;
      form.data.billing_postcode = pilot.billing_postcode;

      states.forEach((item) => {
        if (item.value == pilot.billing_state) stateSelectedValue = item;
      });

      setTimeout(() => {
        form.dirty = false;
      }, 250);

      pilot.name = FormatPilotName(pilot.firstname, pilot.middlenames, pilot.lastname);

      if (pilot.birthdate) {
        let dob = new Date(pilot.birthdate);
        let month = dob.getMonth() + 1;
        pilot.birthdate =
          month.toString().padStart(2, "0") + "/" + dob.getDate().toString().padStart(2, "0") + "/" + dob.getFullYear();
      }

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  });

  onDestroy(() => {
    ScrollingEnable();
  });

  afterUpdate(() => {
    M.updateTextFields();
  });

  async function FieldChanged(field) {
    form.dirty = true;
  }

  function ValidateForm() {
    return new Promise(function (resolve, reject) {
      form.schema
        .validate(form.data, { abortEarly: false })
        .then((values) => {
          form.valid = true;
          form.errors = {};
          resolve();
        })
        .catch((validationErrors) => {
          form.valid = false;
          form.errors = {};

          for (let i = 0; i < validationErrors.inner.length; i++) {
            let error = validationErrors.inner[i];
            form.errors[error.path] = {
              type: error.type,
              message: typeof error.errors[0] == "object" ? error.errors[0].message : error.errors[0],
            };
          }

          reject("InvalidForm");
        });
    });
  }

  async function Cancel() {
    if (form.dirty) {
      let discard = await myModal.showConfirm("Unsaved changes", "Some changes have not been saved yet.", {
        buttonOk: "Discard",
        buttonCancel: "Cancel",
      });
      if (discard) dispatch("close", {});
    } else dispatch("close", {});
  }

  async function Save() {
    form.submitted = true;

    try {
      await ValidateForm();
      busy = true;
      let fields = {
        phone: form.data.phone ? form.data.phone.trim() : null,
        billing_city: form.data.billing_city ? form.data.billing_city.trim() : null,
        billing_state: form.data.billing_state ? form.data.billing_state.trim() : null,
        billing_country: form.data.billing_country ? form.data.billing_country.trim() : null,
        billing_postcode: form.data.billing_postcode ? form.data.billing_postcode.trim() : null,
      };
      await restPilots.PutPilots($appSession.pilotId, fields);
      busy = false;
      dispatch("close", {});
    } catch (error) {
      busy = false;

      switch (error) {
        case "InvalidForm":
          break;
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  }
</script>

<style>
  .section-header {
    color: #000;
    text-transform: uppercase;
    font-size: 0.8em;
    font-weight: 500;
    margin-top: 20px;
    margin-bottom: 15px;
  }

  .field-group {
    margin-bottom: 8px;
  }
  .field-group label {
    font-size: 0.8em;
    display: block;
  }
</style>

<div class="overlay-page-wrapper" transition:fly={{ y: 200, duration: 500 }}>
  <div class="overlay-page-header valign-wrapper">
    <div class="container">

      <div class="row">
        <div class="col s10 offset-s1">
          <h5 class="center-align">Details</h5>
        </div>
      </div>

    </div>
  </div>

  <div class="overlay-page-body">
    <div class="container">

      {#if pilot}
        <div class="row">
          <div class="col s12 m6">

            <div class="section-header" style="margin-top: 0px;">Personal</div>

            <div class="field-group">
              <label>Name</label>
              {pilot.name}
            </div>

            <div class="field-group">
              <label>Date of birth</label>
              {pilot.birthdate}
            </div>

            <div class="section-header">Contact</div>

            <div class="input-field">
              <input type="tel" bind:value={form.data.phone} on:change={() => FieldChanged('phone')} />
              <label>Phone</label>
              {#if form.errors && form.errors.phone}
                <div class="helper-text red-text">{form.errors.phone.message}</div>
              {/if}
            </div>

            <div class="input-field">
              <input type="text" bind:value={form.data.billing_city} on:change={() => FieldChanged('billing_city')} />
              <label>City</label>
              {#if form.errors && form.errors.billing_city}
                <div class="helper-text red-text">{form.errors.billing_city.message}</div>
              {/if}
            </div>

            <label class="active" style="display: block; margin-bottom: 10px;">State</label>
            <div class="svelte-select">
              <Select
                items={states}
                bind:selectedValue={stateSelectedValue}
                isSearchable={true}
                placeholder="Select..."
                isClearable={true}
                showChevron={true}
                on:select={() => {
                  FieldChanged('billing_state');
                }}
                on:clear={() => {
                  FieldChanged('billing_state');
                  stateSelectedValue = null;
                  form.data.billing_state = '';
                }} />
            </div>
            <div class="input-field" style="margin-top: 0.5em;">
              {#if form.errors && form.errors.billing_state}
                <span class="helper-text red-text">{form.errors.billing_state.message}</span>
              {/if}
            </div>

            <div class="input-field" style="margin-top: 30px;">
              <input
                type="text"
                autocomplete="off"
                bind:value={form.data.billing_country}
                on:change={() => FieldChanged('billing_country')}
                disabled />
              <label>Country</label>
              {#if form.errors && form.errors.billing_country}
                <div class="helper-text red-text">{form.errors.billing_country.message}</div>
              {/if}
            </div>

            <div class="input-field">
              <input
                type="text"
                autocomplete="off"
                bind:value={form.data.billing_postcode}
                on:change={() => FieldChanged('billing_postcode')}
                maxlength="5" />
              <label>Zip/Post Code</label>
              {#if form.errors && form.errors.billing_postcode}
                <div class="helper-text red-text">{form.errors.billing_postcode.message}</div>
              {/if}
            </div>

            <div class="section-header">Employment</div>

            <div class="field-group">
              <label>Employee ID</label>
              {pilot.employeeid}
            </div>

            <div class="field-group">
              <label>Certification number</label>
              {pilot.certificationnumber}
            </div>

            <div class="field-group">
              Has KCM
              {#if pilot.kcmrated == 1}
                <i class="material-icons">done</i>
              {:else}
                <i class="material-icons">clear</i>
              {/if}
            </div>

            <div class="field-group">
              In CASS
              {#if pilot.cassrated == 1}
                <i class="material-icons">done</i>
              {:else}
                <i class="material-icons">clear</i>
              {/if}
            </div>

          </div>

        </div>
      {/if}
    </div>

  </div>

  <div class="overlay-page-footer">

    <div class="container">
      {#if busy}
        <div class="preloader-wrapper small active">
          <div class="spinner-layer spinner-green-only">
            <div class="circle-clipper left">
              <div class="circle" />
            </div>
            <div class="gap-patch">
              <div class="circle" />
            </div>
            <div class="circle-clipper right">
              <div class="circle" />
            </div>
          </div>
        </div>
      {/if}

      <div class="right">
        <button class="waves-effect btn-flat" disabled={busy} on:click={Cancel}>Cancel</button>

        <button class="waves-effect btn blue-grey" disabled={busy} on:click={Save}>Save</button>
      </div>
    </div>

  </div>

  <MyModal bind:this={myModal} />
  <RestPilots bind:this={restPilots} />
</div>
